import { z } from '@customerio/hydra-validation';
import type { SearchResult } from '@/services/auth/types';

/**
 * A universal-search row carries the discriminant every renderer reads first.
 * Anything without one cannot be grouped, headed or iconed, so it is dropped.
 */
const searchResultRow = z.object({ kind: z.string().min(1) });

/**
 * The generated universal-search model widens `kind`, `url` and `other` to
 * nullable strings, so a response row is not a `SearchResult` to the compiler.
 *
 * The check is deliberately "an object carrying a non-empty string `kind`"
 * rather than membership of the `SearchResult` union. The dialog renders an
 * unrecognized kind through its fallbacks — its own group in `result-grouping`,
 * a `Search results for '<kind>'` heading in `result-metadata`, the `search`
 * icon in `result-icons`, and a `?? false` quick-look guard — so gating on the
 * union would make a kind the backend ships before this union is updated vanish
 * from the dialog instead of appearing generically.
 */
const searchResultSchema = z.custom<SearchResult>((value) => searchResultRow.safeParse(value).success);

/** @public */
export function parseSearchResults(rows: readonly unknown[]): SearchResult[] {
  return rows.map((row) => searchResultSchema.safeParse(row)).flatMap((r) => (r.success ? [r.data] : []));
}
